import { createTheme, Theme, ThemeOptions } from "@mui/material/styles";
import { MuiTextFieldVariantsOverridden } from "../modules/mui/TextFields";
import { BookingApi } from "../API/Apis/BookingApi";

export interface ExtraColors {
  white: true;
  blue: true;
  inputFieldBorder: true;
  gray: true;
  red: true;
  green: true;
  "success-2": true;
  "primary-2": true;
  clear: true;
  secondaryColor: true;
  ternaryColor: true;
}

// Use the same default color for all variables to keep the UI consistent if the API call fails.
const safeColor = (value: string, fallback: string): string =>
  value && typeof value === "string" && value.trim() !== ""
    ? value.trim()
    : fallback;

let primaryVar = "#e6e6e6";
let secondaryVar = "#e6e6e6";
let ternaryVar = "#e6e6e6";
let backgroundVar = "#e6e6e6";
let primaryTextVar = "#000000";
let secondaryTextVar = "#000000";
let ternaryTextVar = "#000000";
let backgroundTextVar = "#e6e6e6";
let isThemeUpdated = "false";
let confirmationTextVar = secondaryVar;
let isGretaNewUiVar = false;

export const setIsGretaNewUi = (value: boolean) => {
  isGretaNewUiVar = value;
};

// global ThemeProvider can re-render instead of keeping the initial gray theme.
type ThemeListener = () => void;
const themeListeners = new Set<ThemeListener>();

export const onThemeUpdated = (cb: ThemeListener): (() => void) => {
  themeListeners.add(cb);
  return () => {
    themeListeners.delete(cb);
  };
};

const notifyThemeUpdated = () => {
  themeListeners.forEach((cb) => cb());
};

const getDefaultPalette = () => ({
  secondaryColor: { main: secondaryVar, contrastText: secondaryTextVar },
  white: { main: "#ffffff" },
  primary: { main: primaryVar, contrastText: primaryTextVar },
  blue: { main: "#2563eb" },
  inputFieldBorder: { main: "#e6e6e6" },
  gray: { main: "#7a7c80" },
  red: { main: "#ff1717" },
  green: { main: "#1a7a67" },
  "success-2": { main: "#33B627" },
  "primary-2": { main: "#DBE7E9" },
  clear: { main: "transparent" },
  ternaryColor: { main: ternaryVar, contrastText: ternaryTextVar },
  backgroundColor: { main: backgroundVar, contrastText: backgroundTextVar },
  confirmationTextColor: { contrastText: confirmationTextVar },
});

const defaultPalette = getDefaultPalette();

let customTheme: ThemeOptions = {
  components: {
    MuiTextField: {
      variants: MuiTextFieldVariantsOverridden,
    },
    MuiInputLabel: {
      styleOverrides: {
        asterisk: {
          color: "red",
        },
        root: {
          zIndex: "auto",
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: "red",
        },
        root: {
          zIndex: "auto",
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: "black",
        },
      },
    },
  },
  palette: defaultPalette,
};

export const formatTheme = () => {
  if (isThemeUpdated == "false") {
    isThemeUpdated = "true";
    BookingApi.fetchTheme().then((e) => {
      primaryVar = e.data.primaryColor;
      secondaryVar = e.data.secondaryColor;
      ternaryVar = e.data.ternaryColor;
      backgroundVar = e.data.backgroundColor;
      backgroundTextVar = e.data.backgroundTextColor;
      primaryTextVar = e.data.primaryTextColor;
      secondaryTextVar = e.data.secondaryTextColor;
      ternaryTextVar = e.data.ternaryTextColor;
      confirmationTextVar = e.data.confirmationPageTextColor;
      const dynamicTheme = updateTheme(
        primaryVar,
        secondaryVar,
        ternaryVar,
        backgroundVar,
        backgroundTextVar,
        primaryTextVar,
        secondaryTextVar,
        ternaryTextVar,
        confirmationTextVar
      );
      notifyThemeUpdated();
      return dynamicTheme;
    });
  }
  return updateTheme(
    primaryVar,
    secondaryVar,
    ternaryVar,
    backgroundVar,
    backgroundTextVar,
    primaryTextVar,
    secondaryTextVar,
    ternaryTextVar,
    confirmationTextVar
  );
};

export const formatThemeForReservedArea = async () => {
  if (isThemeUpdated == "false") {
    isThemeUpdated = "true";
    await BookingApi.fetchTheme().then((e) => {
      primaryVar = e.data.primaryColor;
      secondaryVar = e.data.secondaryColor;
      ternaryVar = e.data.ternaryColor;
      backgroundVar = e.data.backgroundColor;
      backgroundTextVar = e.data.backgroundTextColor;
      primaryTextVar = e.data.primaryTextColor;
      secondaryTextVar = e.data.secondaryTextColor;
      ternaryTextVar = e.data.ternaryTextColor;
      confirmationTextVar = e.data.confirmationPageTextColor;
      const dynamicTheme = updateTheme(
        primaryVar,
        secondaryVar,
        ternaryVar,
        backgroundVar,
        backgroundTextVar,
        primaryTextVar,
        secondaryTextVar,
        ternaryTextVar,
        confirmationTextVar
      );
      notifyThemeUpdated();
      return dynamicTheme;
    });
  }
  return updateTheme(
    primaryVar,
    secondaryVar,
    ternaryVar,
    backgroundVar,
    backgroundTextVar,
    primaryTextVar,
    secondaryTextVar,
    ternaryTextVar,
    confirmationTextVar
  );
};

const updateTheme = (
  bookingPrimaryColor,
  bookingSecondaryColor,
  bookingTernaryColor,
  bookingBackgroundColor,
  backgroundTextColor,
  primaryTextColor,
  secondaryTextColor,
  ternaryTextColor,
  confirmationPageTextColor
) => {
  const updatedPalette = {
    ...getDefaultPalette(),
    primary: {
      main: safeColor(bookingPrimaryColor, "#28B330"),
      contrastText: safeColor(primaryTextColor, "#FFFFFF"),
    },
    secondaryColor: {
      main: safeColor(bookingSecondaryColor, "#FF238C"),
      contrastText: safeColor(secondaryTextColor, "#FFFFFF"),
    },
    ternaryColor: {
      main: safeColor(bookingTernaryColor, "#31C634"),
      contrastText: safeColor(ternaryTextColor, "#FFFFFF"),
    },
    backgroundColor: {
      main: safeColor(bookingBackgroundColor, "#FFFFFF"),
      contrastText: safeColor(backgroundTextColor, "#000000"),
    },
    confirmationTextColor: {
      contrastText: safeColor(confirmationPageTextColor, "#000000"),
    },
  };

  const updatedTheme: ThemeOptions = {
    components: {
      MuiTextField: {
        variants: MuiTextFieldVariantsOverridden,
      },
      MuiInputLabel: {
        styleOverrides: {
          asterisk: {
            color: "red",
          },
          root: {
            zIndex: "auto",
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: {
            color: "red",
          },
          root: {
            zIndex: "auto",
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: "black",
          },
        },
      },
      ...(isGretaNewUiVar && {
        MuiPickersDay: {
          styleOverrides: {
            root: {
              "&.Mui-selected": {
                backgroundColor: bookingPrimaryColor,
                color: primaryTextColor,
                "&:hover": {
                  backgroundColor: bookingPrimaryColor,
                  filter: "brightness(0.85)",
                },
                "&:focus": {
                  backgroundColor: bookingPrimaryColor,
                },
              },
            },
          },
        },
      }),
    },
    palette: updatedPalette,
  };
  customTheme = { ...customTheme, ...updatedTheme };
  return customTheme;
};

const theme: Theme = createTheme(customTheme);
export default theme;
